import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";

import { getAddOnIdentityKey } from "@/lib/addOnIdentity";
import { useStripeCheckout } from "@/hooks/useCheckout";
import { DEFAULT_ADD_ONS, getAddOns } from "@/lib/api";
import type { CartAddOnItem, CartBundleItem, CartCourseItem, CartItem } from "@/lib/schema";
import { useAppStore } from "@/lib/store";

const cartPreviewIcon =
  "https://cdn.prod.website-files.com/686229256a807206c1874545/688cba2c30eb8836dfffcc73_Play%20button.svg";

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

interface AddOnRow {
  id: string;
  name: string;
  description: string | null;
  price: number;
  price_label: string | null;
  image_url: string | null;
  booking_url: string | null;
  is_active: boolean | null;
  sort_order: number | null;
  created_at: string | null;
}

export function CartDrawer() {
  const { cartItems, cartOpen, addToCart, removeFromCart, setCartOpen } = useAppStore();
  const { execute: startCheckout, loading, error } = useStripeCheckout();
  const [availableAddOns, setAvailableAddOns] = useState<AddOnRow[]>(DEFAULT_ADD_ONS);

  const courseItems = cartItems.filter((i): i is CartCourseItem => i.type === "course");
  const bundleItems = cartItems.filter((i): i is CartBundleItem => i.type === "bundle");
  const addOnItems = cartItems.filter((i): i is CartAddOnItem => i.type === "add_on");

  // Fetch add-ons when cart opens and has course items
  useEffect(() => {
    if (!cartOpen) return;
    let cancelled = false;
    getAddOns().then(({ data }) => {
      if (!cancelled && data) setAvailableAddOns(data);
    });
    return () => { cancelled = true; };
  }, [cartOpen]);

  useEffect(() => {
    if (!cartOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCartOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [cartOpen, setCartOpen]);

  if (!cartOpen) return null;

  const courseTotal = courseItems.reduce((sum, item) => sum + item.price, 0);
  const bundleTotal = bundleItems.reduce((sum, item) => sum + item.price, 0);
  const addOnTotal = addOnItems.reduce((sum, item) => sum + item.price, 0);
  const tutorialDiscount = courseItems.length >= 2 ? courseItems.length * 10 : 0;
  const total = courseTotal + bundleTotal + addOnTotal - tutorialDiscount;
  const hasTutorialProducts = courseItems.length > 0 || bundleItems.length > 0;

  const addOnKeysInCart = new Set(addOnItems.map((item) => getAddOnIdentityKey(item)));
  const suggestedAddOns = availableAddOns.filter((addOn) => !addOnKeysInCart.has(getAddOnIdentityKey(addOn)));

  const handleAddAddOn = (addon: AddOnRow) => {
    addToCart({
      type: "add_on",
      add_on_id: addon.id,
      name: addon.name,
      price: addon.price,
      image_url: addon.image_url ?? undefined,
    });
  };

  const getItemKey = (item: CartItem) =>
    item.type === "course"
      ? `course:${item.course_id}`
      : item.type === "bundle"
        ? `bundle:${item.bundle_id}`
        : `addon:${getAddOnIdentityKey(item)}`;

  return (
    <>
      <button
        type="button"
        aria-label="Close cart"
        className="fixed inset-0 z-50 bg-[rgba(29,13,24,0.58)]"
        onClick={() => setCartOpen(false)}
      />
      <aside
        className="fixed bottom-4 right-4 top-4 z-[60] flex w-[min(27rem,calc(100vw-2rem))] flex-col rounded-[1.75rem] bg-white shadow-[0_40px_120px_rgba(31,11,23,0.28)] max-[479px]:inset-[0.5rem] max-[479px]:top-[0.5rem] max-[479px]:w-auto max-[479px]:rounded-[1.3rem]"
        aria-label="Cart"
        aria-modal="true"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#ede4e9] p-[1.35rem]">
          <div>
            <p className="m-0 text-[0.78rem] font-[800] uppercase tracking-[0.08em] text-[#9b8795]">Your Cart</p>
            <h2 className="mt-[0.2rem] mb-0 font-display text-[1.6rem] leading-none text-[#341928] min-[480px]:text-[2rem]">Ready to check out?</h2>
          </div>
          <button
            type="button"
            className="inline-flex h-[2.3rem] w-[2.3rem] items-center justify-center rounded-full bg-[#faf5f8] text-[#4a193c]"
            aria-label="Close cart"
            onClick={() => setCartOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-[1.1rem_1.35rem]">
          {cartItems.length === 0 ? (
            <div className="flex h-full min-h-[14rem] items-center justify-center rounded-[1.25rem] border border-dashed border-[#dcc6d3] text-[#7b7176]">Cart is empty</div>
          ) : (
            <div className="flex flex-col gap-[0.9rem]">
              {cartItems.map((item) => (
                <div key={getItemKey(item)} className="grid grid-cols-[3.5rem_minmax(0,1fr)] items-start gap-[0.9rem] rounded-[1.15rem] border border-[#efe7eb] bg-[#fcfafb] p-[0.9rem]">
                  <img
                    src={
                      item.type === "add_on" && item.image_url
                        ? item.image_url
                        : item.type === "bundle" && item.thumbnail_url
                          ? item.thumbnail_url
                          : cartPreviewIcon
                    }
                    alt=""
                    className="h-14 w-14 rounded-[0.8rem] object-cover"
                  />
                  <div className="min-w-0">
                    <div className="text-base font-bold text-[#341928]">{item.name}</div>
                    {item.type === "bundle" ? (
                      <div className="mt-[0.15rem] text-[0.82rem] font-semibold text-[#8b6d7a]">
                        Bundle of {item.course_count} tutorial{item.course_count === 1 ? "" : "s"}
                      </div>
                    ) : null}
                    <div className="mt-[0.2rem] text-[0.96rem] font-semibold text-[#5f5460]">
                      {priceFormatter.format(item.price)}
                    </div>
                    <button
                      type="button"
                      className="mt-[0.35rem] text-[0.88rem] font-bold text-[#8f3d5f]"
                      onClick={() => removeFromCart(getItemKey(item))}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add-on suggestions */}
          {hasTutorialProducts && suggestedAddOns.length > 0 && (
            <div className="mt-5">
              <p className="m-0 mb-2 text-[0.78rem] font-[800] uppercase tracking-[0.08em] text-[#9b8795]">Enhance Your Experience</p>
              <div className="flex gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {suggestedAddOns.map((addon) => (
                  <div
                    key={addon.id}
                    className="flex w-[10.5rem] flex-none flex-col rounded-[1rem] border border-[#efe7eb] bg-[#fcfafb] p-3"
                  >
                    {addon.image_url && (
                      <img
                        src={addon.image_url}
                        alt={addon.name}
                        className="mb-2 aspect-[3/2] w-full rounded-[0.6rem] object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className="text-[0.82rem] font-bold text-[#341928]">{addon.name}</div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-[0.88rem] font-semibold text-[#5f5460]">
                        {priceFormatter.format(addon.price)}
                      </span>
                      {addon.price_label && (
                        <span className="text-[0.7rem] text-[#9b8795]">{addon.price_label}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center justify-center gap-1 rounded-full border border-[#762a5c] bg-white px-3 py-[0.4rem] text-[0.78rem] font-semibold text-[#762a5c] transition-colors hover:bg-[#faf1f6]"
                      onClick={() => handleAddAddOn(addon)}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-[#ede4e9] p-[1.35rem]">
          {courseItems.length >= 2 ? (
            <>
              <div className="flex items-center justify-between text-[0.88rem] text-[#7e7379]">
                <span>
                  Subtotal ({courseItems.length} tutorial{courseItems.length === 1 ? "" : "s"}
                  {bundleItems.length > 0 ? ` + ${bundleItems.length} bundle${bundleItems.length === 1 ? "" : "s"}` : ""}
                  {addOnItems.length > 0 ? ` + ${addOnItems.length} add-on${addOnItems.length === 1 ? "" : "s"}` : ""})
                </span>
                <span className="line-through">{priceFormatter.format(courseTotal + bundleTotal + addOnTotal)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-[0.88rem] font-semibold text-[#2d8a4e]">
                <span>2+ tutorial discount (−$10 each)</span>
                <span>−{priceFormatter.format(tutorialDiscount)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-base text-[#351c2a]">
                <span className="font-semibold">Total</span>
                <strong>{priceFormatter.format(total)}</strong>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between text-base text-[#351c2a]">
                <span>Total price</span>
                <strong>{priceFormatter.format(total)}</strong>
              </div>
              {courseItems.length > 0 && courseItems.length < 2 && (
                <p className="mt-[0.65rem] mb-0 rounded-[0.6rem] bg-[#f2a435]/10 px-3 py-2 text-center text-[0.84rem] font-semibold text-[#9a6b12]">
                  Add {2 - courseItems.length} more{" "}
                  {2 - courseItems.length === 1 ? "tutorial" : "tutorials"} to unlock{" "}
                  <span className="font-extrabold">$10 off each</span>!
                </p>
              )}
            </>
          )}
          {error ? <p className="mt-[0.7rem] mb-0 text-[0.88rem] font-bold text-[#b24f44]">{error}</p> : null}
          <button
            type="button"
            className="mt-4 w-full rounded-full bg-[#762a5c] px-4 py-[0.95rem] font-[800] text-white disabled:opacity-60"
            disabled={cartItems.length === 0 || loading}
            onClick={() => void startCheckout()}
          >
            {loading ? "Redirecting..." : "Continue to Checkout"}
          </button>
        </div>
      </aside>
    </>
  );
}
